import { forOwn, isPlainObject } from 'lodash';

import { Workflow } from 'dashboard/models/workflow';
import {
  WorkflowDeployment,
  WorkflowDeploymentConfigurationLabel,
} from 'dashboard/models/workflow-deployment';
import {
  FakeWorkflowStepType,
  WorkflowActionEvaluateCodeReturnType,
  WorkflowStep,
  WorkflowStepConditional,
  WorkflowStepLoop,
  WorkflowStepParallel,
  WorkflowStepType,
} from 'dashboard/models/workflow-version';

export const hasBranches = (
  step: WorkflowStep,
): step is WorkflowStepParallel | WorkflowStepConditional => {
  return (
    step.type !== FakeWorkflowStepType.MapOutput &&
    [WorkflowStepType.Parallel, WorkflowStepType.Conditional].includes(step.type)
  );
};

export const hasBranch = (step: WorkflowStep): step is WorkflowStepLoop => {
  return step.type === WorkflowStepType.Loop;
};

/** Snakifies a path. */
export const snakify = (toSnake: string) => {
  return toSnake?.replace(/-/g, '_');
};

export const deepTransformKeys = (
  object: Record<string, any>,
  transform = snakify,
): Record<string, any> => {
  const transformed: Record<string, any> = {};

  forOwn(object, (val, key) => {
    if (isPlainObject(val)) {
      val = deepTransformKeys(val, transform);
    }
    transformed[transform(key)] = val;
  });

  return transformed;
};

// Returns the workflow version id given the following priority order:
// latest draft version, treatment version from an active deployment, latest published version.
export const getPriorityWorkflowVersion = (
  workflow: Workflow,
  workflowDeploymentMap: Record<string, WorkflowDeployment>,
): string => {
  return (
    workflow.relationships['latest-draft-version'].data?.id ??
    workflowDeploymentMap[workflow.relationships['active-deployment'].data?.id]?.attributes[
      'configuration'
    ]?.versions?.find((version) => version.label === WorkflowDeploymentConfigurationLabel.Treatment)
      ?.token ??
    workflow.relationships['latest-published-version'].data?.id
  );
};

export const jsonSchemaToEvalCodeReturnType = (
  schemaString: string,
): WorkflowActionEvaluateCodeReturnType => {
  const schemaType = JSON.parse(schemaString)['type'];

  if (['string', 'number', 'boolean', 'object'].includes(schemaType)) {
    return schemaType;
  }
  return 'void';
};
